import { useEffect, useRef, useState } from "react";
import { caretOffsetFromPoint, placeCaretInInput } from "./caretFromPoint";
import { handleFieldEnterKey } from "./fieldKeyboard";

export interface InlineEditableProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  as?: "h1" | "h2" | "span" | "p" | "strong";
  readOnly?: boolean;
  placeholder?: string;
  title?: string;
  /** When true, empty input saves as "" instead of the placeholder. */
  allowEmpty?: boolean;
  /** Fill the available width so the click target and edit underline span the row. */
  fill?: boolean;
}

/** Click-to-edit title. Enter/blur saves; Escape cancels. */
export function InlineEditable({
  value,
  onChange,
  className,
  as: Tag = "span",
  readOnly,
  placeholder = "Uden titel",
  title = "Klik for at redigere",
  allowEmpty = false,
  fill = false,
}: InlineEditableProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    const empty = !value;
    placeCaretInInput(el, empty ? 0 : caretRef.current, empty ? "start" : "end");
  }, [editing, value]);

  function commit() {
    caretRef.current = null;
    const trimmed = draft.trim();
    const next = trimmed || (allowEmpty ? "" : placeholder);
    setEditing(false);
    if (next !== value) onChange(next);
  }

  function cancel() {
    caretRef.current = null;
    setDraft(value);
    setEditing(false);
  }

  if (readOnly) {
    if (allowEmpty && !value) return null;
    return <Tag className={className}>{value || placeholder}</Tag>;
  }

  const fillClass = fill ? " sm-inline-fill" : "";

  if (editing) {
    const charCount = Math.max(1, Array.from(draft).length);
    return (
      <input
        ref={inputRef}
        className={`sm-inline-input${fillClass} ${className ?? ""}`}
        value={draft}
        size={fill ? 1 : charCount}
        aria-label={title}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          handleFieldEnterKey(e);
          if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <Tag
      className={`sm-inline-editable${fillClass} ${className ?? ""}${
        allowEmpty && !value ? " is-placeholder" : ""
      }`}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        caretRef.current = value
          ? caretOffsetFromPoint(e.currentTarget, e.clientX, e.clientY)
          : 0;
        setEditing(true);
      }}
    >
      {value || placeholder}
    </Tag>
  );
}
